import { isEqual } from 'lodash-es'
import { useLayoutEffect, useEffect, useState } from 'react'

const ARROW_LONG_DIMENSION = 24
const ARROW_SHORT_DIMENSION = 12
const VERTICAL_PLACEMENTS = ['top', 'bottom']
const HORIZONTAL_PLACEMENTS = ['left', 'right']

const determinePosition = (
  own,
  parent,
  primaryPlacement,
  secondaryPlacement,
  primaryOffset,
  secondaryOffset,
  disableAutoFlip = false
) => {
  const position = {}
  let adjustedPrimaryPlacement = primaryPlacement

  // Get viewport dimensions
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  // Calculate potential overflow
  const willOverflowRight = (parent.left + parent.width + primaryOffset + own.width) > viewportWidth
  const willOverflowLeft = (parent.left - primaryOffset - own.width) < 0
  const willOverflowTop = (parent.top - primaryOffset - own.height) < 0
  const willOverflowBottom = (parent.top + parent.height + primaryOffset + own.height) > viewportHeight

  // Adjust primary placement if necessary to prevent overflow (unless disabled)
  if (!disableAutoFlip) {
    if (adjustedPrimaryPlacement === 'right' && willOverflowRight) {
      adjustedPrimaryPlacement = 'left'
    } else if (adjustedPrimaryPlacement === 'left' && willOverflowLeft) {
      adjustedPrimaryPlacement = 'right'
    } else if (adjustedPrimaryPlacement === 'top' && willOverflowTop) {
      adjustedPrimaryPlacement = 'bottom'
    } else if (adjustedPrimaryPlacement === 'bottom' && willOverflowBottom) {
      adjustedPrimaryPlacement = 'top'
    }
  }

  // Set position based on adjusted placement
  switch (adjustedPrimaryPlacement) {
    case 'top':
      position.bottom = parent.height + primaryOffset
      break
    case 'bottom':
      position.top = parent.height + primaryOffset
      break
    case 'left':
      position.right = parent.width + primaryOffset
      break
    default:
      position.left = parent.width + primaryOffset
      break
  }

  // Center alignment on secondary (perpendicular) axis
  if (HORIZONTAL_PLACEMENTS.includes(adjustedPrimaryPlacement)) {
    position.top = (parent.height / 2) - (own.height / 2)
  } else {
    position.left = (parent.width / 2) - (own.width / 2)
  }

  // Handle unreasonable secondary positions
  if ((HORIZONTAL_PLACEMENTS.includes(adjustedPrimaryPlacement) && HORIZONTAL_PLACEMENTS.includes(secondaryPlacement))
    || (VERTICAL_PLACEMENTS.includes(adjustedPrimaryPlacement) && VERTICAL_PLACEMENTS.includes(secondaryPlacement))) {
    return { position }
  }

  switch (secondaryPlacement) {
    case 'top':
      position.top += -(own.height / 2) + ARROW_LONG_DIMENSION + secondaryOffset
      break
    case 'bottom':
      position.top += (own.height / 2) - ARROW_LONG_DIMENSION + secondaryOffset
      break
    case 'left':
      position.left += -(own.width / 2) + ARROW_LONG_DIMENSION + secondaryOffset
      break
    case 'right':
      position.left += (own.width / 2) - ARROW_LONG_DIMENSION + secondaryOffset
      break
    default:
      break
  }

  return { position, adjustedPrimaryPlacement }
}

const determineArrowPosition = (
  own,
  primaryPlacement,
  secondaryPlacement,
  arrowOffset = 0
) => {
  const arrowPosition = {}

  switch (primaryPlacement) {
    case 'top':
      arrowPosition.bottom = -ARROW_SHORT_DIMENSION
      arrowPosition.left = (own.width / 2) - (ARROW_LONG_DIMENSION / 2) + arrowOffset
      break
    case 'bottom':
      arrowPosition.top = -ARROW_SHORT_DIMENSION
      arrowPosition.left = (own.width / 2) - (ARROW_LONG_DIMENSION / 2) + arrowOffset
      break
    case 'left':
      arrowPosition.top = (own.height / 2) - (ARROW_LONG_DIMENSION / 2) + arrowOffset
      arrowPosition.right = -ARROW_SHORT_DIMENSION
      break
    default: // right
      arrowPosition.top = (own.height / 2) - (ARROW_LONG_DIMENSION / 2) + arrowOffset
      arrowPosition.left = -ARROW_SHORT_DIMENSION
      break
  }

  // Handle unreasonable secondary positions
  // "left-left", "top-bottom", etc
  if ((HORIZONTAL_PLACEMENTS.includes(primaryPlacement) && HORIZONTAL_PLACEMENTS.includes(secondaryPlacement))
    || (VERTICAL_PLACEMENTS.includes(primaryPlacement) && VERTICAL_PLACEMENTS.includes(secondaryPlacement))) {
    return arrowPosition
  }

  switch (secondaryPlacement) {
    case 'top':
      arrowPosition.bottom = ARROW_SHORT_DIMENSION + arrowOffset
      arrowPosition.top = 'unset'
      break
    case 'bottom':
      arrowPosition.top = ARROW_SHORT_DIMENSION + arrowOffset
      break
    case 'left':
      arrowPosition.right = ARROW_SHORT_DIMENSION + arrowOffset
      arrowPosition.left = 'unset'
      break
    case 'right':
      arrowPosition.left = ARROW_SHORT_DIMENSION + arrowOffset
      break
    default:
      break
  }

  return arrowPosition
}

export const useOutsideClickDetection = (ref, onClickOutside, outsideClickDetectionEnabled = true) => {
  const handleClickOutside = (event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      onClickOutside()
    }
  }

  useEffect(() => {
    if (outsideClickDetectionEnabled) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  })
  return null
}

export function useSpotlightPosition(ref, placement, offset, secondaryOffset, arrowOffset, disableAutoFlip = false) {
  const [position, setPosition] = useState({})
  const [arrowPosition, setArrowPosition] = useState({})
  // We track the “actual” primary placement in case top → bottom, left → right, etc. flips happen
  const [actualPrimaryPlacement, setActualPrimaryPlacement] = useState(placement.split('-')[0])

  useLayoutEffect(() => {
    if (!ref.current) return

    const [primary, secondary] = placement.split('-')
    const parentRect = ref.current.parentNode?.getBoundingClientRect?.() || {}
    const ownRect = ref.current.getBoundingClientRect()

    // Determine new position & possibly flipped placement
    const { position: newPos, adjustedPrimaryPlacement } = determinePosition(
      ownRect,
      parentRect,
      primary,
      secondary,
      offset,
      secondaryOffset,
      disableAutoFlip
    )

    // Update state only if it changed
    if (!isEqual(newPos, position)) {
      setPosition(newPos)
    }

    if (adjustedPrimaryPlacement !== actualPrimaryPlacement) {
      setActualPrimaryPlacement(adjustedPrimaryPlacement)
    }

    // Now do arrow
    const newArrowPos = determineArrowPosition(ownRect, adjustedPrimaryPlacement, secondary, arrowOffset)
    if (!isEqual(newArrowPos, arrowPosition)) {
      setArrowPosition(newArrowPos)
    }
  }, [
    ref,
    placement,
    offset,
    secondaryOffset,
    position,
    arrowPosition,
    actualPrimaryPlacement,
    arrowOffset,
    disableAutoFlip
  ])

  // Recompute if user resizes the window
  useEffect(() => {
    const handleResize = () => {
      setPosition({})
      setArrowPosition({})
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return { position, arrowPosition, actualPrimaryPlacement }
}
